import React, { useState, useEffect, useCallback, useRef } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Bar, LinePath } from '@visx/shape';
import { Group } from '@visx/group';
import { AxisBottom } from '@visx/axis';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { Text } from '@visx/text';
import { Box, Grid } from '@mui/material';
import PropTypes from 'prop-types';

import {
  getParams,
  thousandFormat,
  printFormat,
} from '../../../../Utils/Functions';
import {
  negativeActiveColor,
  negativeDisableColor,
  barHeight,
  axis,
} from '../../../../Assets/js/constant';

// accessors

const y = (d, key) => d[key];

const RightChart = ({
  queryData,
  selectedYears,
  selectedMonths,
  selectedTopItems,
  selectedRightItems,
  filterName,
  handleFilter,
  mChartHeight,
}) => {
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const [resize, setResize] = useState(false);
  const [data, setData] = useState([]);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [selectedRects, setSelectedRects] = useState([]);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    prepareData();
  }, [queryData, selectedYears, selectedMonths, selectedTopItems, filterName]);

  const prepareData = useCallback(() => {
    let dictData = {};
    let newData = [];

    let _selectedTypes = [];
    selectedTopItems.forEach((item) => {
      if (item['type']) {
        _selectedTypes.push(item['type']);
      }
    });

    if (_selectedTypes.length === 0) {
      _selectedTypes = ['Existing', 'New'];
    }

    if (queryData['MiddleRight']) {
      queryData['MiddleRight'].forEach((d) => {
        if (_selectedTypes.indexOf(d.ExistingOrNew) > -1) {
          if (!dictData[d.ExistingOrNew]) {
            dictData[d.ExistingOrNew] = {};
            dictData[d.ExistingOrNew]['ExistingOrNew'] = d.ExistingOrNew;
            dictData[d.ExistingOrNew]['WorkGeneratedAmount'] = 0;
          }

          dictData[d.ExistingOrNew]['WorkGeneratedAmount'] = parseFloat(
            d['WorkGeneratedAmount'],
          );
        }
      });
    }

    Object.keys(dictData).map((key) => newData.push(dictData[key]));

    setData(newData);
  }, [queryData, selectedTopItems]);

  const getColor = useCallback((data, type = '') => {
    const activeColor = type === 'Existing' ? negativeActiveColor : '#1c75bc';
    const disableColor = type === 'New' ? negativeDisableColor : '#ccdce9';

    if (selectedRightItems.length === 0) return activeColor;
    if (selectedRightItems.indexOf(data.ExistingOrNew) > -1) return activeColor;
    return disableColor;
  }, [selectedRightItems]);

  const handleElement = useCallback((event, element, item = null) => {
    let _selectedBars = [],
      _selectedRects = [],
      _selectedRightItems = [];
    let exist = false,
      index = NaN;

    switch (element) {
      case 'bar':
        if (event.shiftKey) {
          _selectedBars = selectedBars.slice();
          _selectedRightItems = selectedRightItems.slice();
          _selectedRects = selectedRects.slice();

          for (let i = 0; i < selectedBars.length; i++) {
            if (selectedBars[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('barActive');
            _selectedBars.push(event.target);
            _selectedRightItems.push(item);
          } else {
            event.target.classList.remove('barActive');
            _selectedBars.splice(index, 1);
            _selectedRightItems.splice(index, 1);
          }
        } else {
          selectedBars.forEach((selectedBar) => {
            selectedBar.classList.remove('barActive');
            if (selectedBar === event.target) exist = true;
          });

          selectedRects.forEach((selectedRect) => {
            selectedRect.classList.remove('bkgActive');
          });

          if (exist && selectedBars.length === 1) {
            _selectedBars = [];
            _selectedRects = [];
            _selectedRightItems = [];
          } else {
            event.target.classList.add('barActive');
            _selectedBars = [event.target];
            _selectedRects = [];
            _selectedRightItems = [item];
          }
        }

        break;

      case 'rect':
        if (event.shiftKey) {
          _selectedBars = selectedBars.slice();
          _selectedRightItems = selectedRightItems.slice();
          _selectedRects = selectedRects.slice();

          for (let i = 0; i < selectedRects.length; i++) {
            if (selectedRects[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('bkgActive');
            _selectedRects.push(event.target);
            _selectedRightItems.push(item);
          } else {
            event.target.classList.remove('bkgActive');
            _selectedRects.splice(index, 1);
            _selectedRightItems.splice(index, 1);
          }
        } else {
          selectedBars.forEach((selectedBar) => {
            selectedBar.classList.remove('barActive');
          });
          selectedRects.forEach((selectedRect) => {
            selectedRect.classList.remove('bkgActive');
            if (selectedRect === event.target) exist = true;
          });

          if (exist && selectedRects.length === 1) {
            _selectedBars = [];
            _selectedRects = [];
            _selectedRightItems = [];
          } else {
            event.target.classList.add('bkgActive');
            _selectedBars = [];
            _selectedRects = [event.target];
            _selectedRightItems = [item];
          }
        }

        break;

      default:
        break;
    }

    setSelectedBars(_selectedBars);
    setSelectedRects(_selectedRects);

    handleFilter({ selectedRightItems: _selectedRightItems });
  }, [selectedBars, selectedRects, selectedRightItems, handleFilter]);

  const deSelectAll = useCallback(() => {
    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    selectedRects.forEach((selectedRect) => {
      selectedRect.classList.remove('bkgActive');
    });

    setSelectedBars([]);
    setSelectedRects([]);
    localStorage.removeItem('wgSelectedRightItems');
    handleFilter({ selectedRightItems: [] });
  }, [selectedBars, selectedRects, handleFilter]);

  const showTooltipHandler = useCallback((event, data, type, isBar = true) => {
    let tooltipData, top, left;

    top = event.nativeEvent.layerY - 20;
    left = event.nativeEvent.layerX + 100;
    tooltipData = data;
    tooltipData['isBar'] = isBar;
    tooltipData['type'] = type;
    
    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
    }

    showTooltip({
      tooltipData: tooltipData,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');
    setHoverBar(null);

    hideTooltip();
  }, [hoverBar, hideTooltip]);

  const count = data.length;
  const width = window.innerWidth / 2 - 4;
  const height = mChartHeight - 12;
  const margin = {
    top: 0,
    right: 10,
    bottom: 0,
    left: 0,
  };
  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );

  let maxValue = 0;
  let minValue = 0;
  if (count > 0) {
    data.forEach((d) => {
      maxValue = Math.max(maxValue, d.WorkGeneratedAmount);
      minValue = Math.min(minValue, d.WorkGeneratedAmount);
    });
  }

  // scales
  const yScale = scaleLinear({
    domain: [minValue * 1.2, maxValue * 1.2],
    range: [yMax, 30],
    nice: true,
  });

  const xScale = scaleBand()
    .domain(data.map((d) => y(d, 'ExistingOrNew')))
    .rangeRound([xMax, 0])
    .padding(0.2);

  return (
    <>
      {count > 0 ? (
        <Grid container style={{ paddingTop: 10 }}>
          <svg width={width > 0 ? width : 0} height={height}>
            <rect
              width={width > 0 ? width : 0}
              height={height}
              fill={'white'}
              onClick={deSelectAll}
            />
            <Group top={margin.top} left={margin.left}>
              {data.map((d, i) => {
                let y0 = xScale(0),
                  h0 = 0,
                  a = 0;
                d['WorkGeneratedAmount'] === undefined
                  ? (a = 0)
                  : (a = d['WorkGeneratedAmount']);

                if (a >= 0) {
                  y0 = yScale(a);
                  h0 = yScale(0) - y0;
                } else {
                  y0 = yScale(0);
                  h0 = yScale(a) - yScale(0);
                }

                let w0 = xScale.bandwidth() - 2;
                let x0 = xScale(y(d, 'ExistingOrNew'));

                return (
                  <Group
                    key={`${filterName}-${y(d, 'WorkGeneratedAmount')}-${i}`}
                  >
                    <Bar
                      x={x0}
                      y={y0}
                      width={w0 > 0 ? w0 : 0}
                      height={h0}
                      fill={getColor(d, d['ExistingOrNew'])}
                      onClick={(event) => {
                        handleElement(event, 'bar', d['ExistingOrNew']);
                      }}
                      onMouseMove={(event) => {
                        showTooltipHandler(event, d, d['ExistingOrNew']);
                      }}
                      onMouseLeave={hideTooltipHandler}
                      onTouchMove={(event) => {
                        showTooltipHandler(event, d, d['ExistingOrNew']);
                      }}
                      onTouchEnd={hideTooltipHandler}
                    />

                    <Text
                      x={x0 + w0 / 2 - 10}
                      y={y0 + (a < 0 ? h0 + 15 : 0)}
                      verticalAnchor="end"
                      textAnchor="start"
                      fontSize={11}
                      dx={-5}
                      dy={-5}
                    >
                      {printFormat.millions(d['WorkGeneratedAmount'] / 1000000)}
                    </Text>

                    <LinePath
                      data={[
                        { x: 0, y: yScale(0) },
                        { x: xMax, y: yScale(0) },
                      ]}
                      x={(d) => (d.x ? d.x : 0)}
                      y={(d) => (d.y ? d.y : 0)}
                      stroke={'#555555'}
                      strokeDasharray="2,1"
                    />
                  </Group>
                );
              })}
            </Group>
          </svg>

          {tooltipOpen && (
            <TooltipWithBounds
             
              top={tooltipTop + pageYOffset}
              left={tooltipLeft}
            >
              {tooltipData.isBar ? (
                <div className="bkg-white color-black">
                  <div className="pdv-5">
                    Work Generated Category:{' '}
                    <strong> {tooltipData['type']}</strong>
                  </div>
                  <div className="ft-12">
                    Work Gen Amount:{' '}
                    <strong>
                      ${thousandFormat(tooltipData['WorkGeneratedAmount'])}
                    </strong>
                  </div>
                </div>
              ) : (
                <div></div>
              )}
            </TooltipWithBounds>
          )}

          <div style={axis}>
            <svg width={width > 0 ? width : 0} height={barHeight}>
              <rect
                x={0}
                y={0}
                width={xMax > 0 ? xMax : 0}
                height={barHeight}
                fill={'transparent'}
                onClick={(event) => {
                  deSelectAll();
                }}
              />
              <AxisBottom
                hideTicks={true}
                scale={xScale}
                top={0}
                tickLabelProps={(value, index) => ({
                  fontSize: 11,
                  textAnchor: 'middle',
                  dy: '-0.5em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text {...tickProps} onClick={deSelectAll}>
                    {formattedValue}
                  </text>
                )}
              />
            </svg>
          </div>
        </Grid>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 180 }}>
          No data available
        </Box>
      )}
    </>
  );
};

RightChart.propTypes = {
  queryData: PropTypes.object.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedRightItems: PropTypes.array.isRequired,
  filterName: PropTypes.string.isRequired,
  handleFilter: PropTypes.func.isRequired,
  mChartHeight: PropTypes.number.isRequired,
};

export default RightChart;
